import { APIProvider } from "../../../../AppController";
import { APIRoutes } from "../../../../config";
import {
  ICreateTelehealthSessionRequest,
  ICreateTelehealthSessionResponse,
  IGetTelehealthSessionResponse,
  IPublicTelehealthJoinResponse,
  ITelehealthAssignableProvidersResponse,
  ITelehealthAssignableTechniciansResponse,
  ITelehealthContactInfoResponse,
  ITelehealthDashboardRequest,
  ITelehealthDashboardResponse,
  ITelehealthLink,
  ITelehealthPatientSessionsResponse,
  ITelehealthRepository,
  ITelehealthSmsOutcomesResponse,
  ITelehealthTokenResponse,
  ITelehealthVisitSessionMapResponse,
  IUpdateTelehealthSessionRequest,
} from "../../../core/ports/Telehealth";

export const TelehealthRepositoryFactory = ({
  apiProvider,
}: {
  apiProvider: typeof APIProvider;
}): ITelehealthRepository => {
  const createSession = (
    request: ICreateTelehealthSessionRequest
  ): Promise<ICreateTelehealthSessionResponse> => {
    return apiProvider.POST(APIRoutes.telehealthSessions, request);
  };

  const getSessionByVisitNote = (
    visitNoteId: number
  ): Promise<IGetTelehealthSessionResponse> => {
    return apiProvider.GET(
      APIRoutes.telehealthSessionByVisitNote.replace(
        "{visit_note_id}",
        String(visitNoteId)
      )
    );
  };

  const getSessionSmsOutcomes = (
    telehealthSessionId: string,
    limit = 100
  ): Promise<ITelehealthSmsOutcomesResponse> => {
    return apiProvider.GET(
      APIRoutes.telehealthSessionSmsOutcomes.replace(
        "{telehealth_session_id}",
        telehealthSessionId
      ),
      { limit }
    );
  };

  const listDashboardSessions = (
    clinicId: number,
    request: ITelehealthDashboardRequest = {}
  ): Promise<ITelehealthDashboardResponse> => {
    return apiProvider.GET(APIRoutes.telehealthDashboardSessions, {
      clinic_id: clinicId,
      ...request,
    });
  };

  const listAssignableProviders = (
    clinicId: number
  ): Promise<ITelehealthAssignableProvidersResponse> => {
    return apiProvider.GET(
      APIRoutes.telehealthAssignableProviders.replace(
        "{clinic_id}",
        String(clinicId)
      )
    );
  };

  const listAssignableTechnicians = (
    clinicId: number
  ): Promise<ITelehealthAssignableTechniciansResponse> => {
    return apiProvider.GET(
      APIRoutes.telehealthAssignableTechnicians.replace(
        "{clinic_id}",
        String(clinicId)
      )
    );
  };

  const getActiveSessionsByVisitNotes = (
    clinicId: number,
    visitNoteIds: number[]
  ): Promise<ITelehealthVisitSessionMapResponse> => {
    return apiProvider.GET(APIRoutes.telehealthActiveSessionsByVisitNotes, {
      clinic_id: clinicId,
      visit_note_ids: visitNoteIds.join(","),
    });
  };

  const listPatientSessions = (
    clinicId: number,
    patientId: number
  ): Promise<ITelehealthPatientSessionsResponse> => {
    return apiProvider.GET(
      APIRoutes.telehealthPatientSessions.replace(
        "{patient_id}",
        String(patientId)
      ),
      { clinic_id: clinicId }
    );
  };

  const getLatestPatientContact = (
    patientId: number
  ): Promise<ITelehealthContactInfoResponse> => {
    return apiProvider.GET(
      APIRoutes.telehealthPatientLatestContact.replace(
        "{patient_id}",
        String(patientId)
      )
    );
  };

  const updateSession = (
    telehealthSessionId: string,
    request: IUpdateTelehealthSessionRequest
  ): Promise<ICreateTelehealthSessionResponse> => {
    return apiProvider.PATCH(
      APIRoutes.telehealthSession.replace(
        "{telehealth_session_id}",
        telehealthSessionId
      ),
      request
    );
  };

  const getClinicianToken = (
    telehealthSessionId: string
  ): Promise<ITelehealthTokenResponse> => {
    return apiProvider.POST(
      APIRoutes.telehealthClinicianToken.replace(
        "{telehealth_session_id}",
        telehealthSessionId
      ),
      {}
    );
  };

  const getOrRotateLink = (
    telehealthSessionId: string,
    rotate = false
  ): Promise<ITelehealthLink> => {
    return apiProvider.POST(
      APIRoutes.telehealthSessionLinks.replace(
        "{telehealth_session_id}",
        telehealthSessionId
      ),
      { rotate }
    );
  };

  const shareLink = (
    telehealthSessionId: string,
    request: IUpdateTelehealthSessionRequest
  ): Promise<ICreateTelehealthSessionResponse> => {
    return apiProvider.POST(
      APIRoutes.telehealthShareLink.replace(
        "{telehealth_session_id}",
        telehealthSessionId
      ),
      request
    );
  };

  const revokeLink = (
    telehealthSessionId: string
  ): Promise<{ success: boolean }> => {
    return apiProvider.POST(
      APIRoutes.telehealthRevokeLink.replace(
        "{telehealth_session_id}",
        telehealthSessionId
      ),
      {}
    );
  };

  const endSession = (
    telehealthSessionId: string
  ): Promise<{ success: boolean }> => {
    return apiProvider.POST(
      APIRoutes.telehealthEndSession.replace(
        "{telehealth_session_id}",
        telehealthSessionId
      ),
      {}
    );
  };

  const getPublicJoin = (
    token: string
  ): Promise<IPublicTelehealthJoinResponse> => {
    return apiProvider.GET(
      APIRoutes.telehealthPublicJoin.replace("{token}", token),
      { _ts: Date.now() },
      {}
    );
  };

  const getPublicPatientToken = (
    token: string
  ): Promise<ITelehealthTokenResponse> => {
    return apiProvider.POST(
      APIRoutes.telehealthPublicToken.replace("{token}", token),
      {},
      {}
    );
  };

  const publicPatientLeave = (
    token: string,
    participantIdentity: string
  ): Promise<{ success: boolean }> => {
    return apiProvider.POST(
      APIRoutes.telehealthPublicLeave.replace("{token}", token),
      { participant_identity: participantIdentity },
      {}
    );
  };

  return {
    createSession,
    updateSession,
    getSessionByVisitNote,
    getSessionSmsOutcomes,
    listDashboardSessions,
    listAssignableProviders,
    listAssignableTechnicians,
    getActiveSessionsByVisitNotes,
    listPatientSessions,
    getLatestPatientContact,
    getClinicianToken,
    getOrRotateLink,
    shareLink,
    revokeLink,
    endSession,
    getPublicJoin,
    getPublicPatientToken,
    publicPatientLeave,
  };
};
